import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  collection,
  doc,
  onSnapshot,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { onDisconnect, onValue, ref, serverTimestamp as rtdbServerTimestamp, set } from "firebase/database";
import type {
  ChatFirebasePayload,
  ChatMessageDoc,
  ExpressChatMessage,
} from "@/types/expressChat";
import {
  buildChatMessagePayload,
  initFirebaseFromPayload,
  isPresenceOnline,
  mapChatMessageDoc,
  otherParticipantUid,
  otherPresencePath,
  ownPresencePath,
  parsePresenceLastSeenMs,
  pathSegments,
  typingFieldPath,
} from "@/services/chat/firebaseChat";

type UseExpressChatRoomOptions = {
  enabled: boolean;
  canSend: boolean;
  httpSend?: (text: string) => Promise<void>;
  onPermissionDenied?: () => void;
};

const PRESENCE_MS = 8000;
const TYPING_IDLE_MS = 2000;
let presenceSession = 0;

export function useExpressChatRoom(
  firebase: ChatFirebasePayload | null,
  options: UseExpressChatRoomOptions,
) {
  const { enabled, canSend, httpSend, onPermissionDenied } = options;
  const [messages, setMessages] = useState<ExpressChatMessage[]>([]);
  const [isPeerOnline, setIsPeerOnline] = useState(false);
  const [peerLastSeenAt, setPeerLastSeenAt] = useState<number | null>(null);
  const [isPeerTyping, setIsPeerTyping] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const typingIdleRef = useRef<number | null>(null);
  const typingOnRef = useRef(false);
  const firebaseRef = useRef(firebase);
  firebaseRef.current = firebase;
  const onPermissionDeniedRef = useRef(onPermissionDenied);
  onPermissionDeniedRef.current = onPermissionDenied;

  const customToken = firebase?.custom_token ?? null;
  const uid = firebase?.uid ?? null;
  const messagesPath = firebase?.messages_path ?? null;
  const roomPath = firebase?.room_path ?? null;
  const presencePath = firebase?.presence_path ?? null;
  const firebaseEnabled = firebase?.enabled !== false;
  const canListen = Boolean(
    enabled && firebaseEnabled && customToken && messagesPath && roomPath && uid,
  );

  const typingPath = typingFieldPath(firebase?.typing_field, firebase?.uid);

  const clearTyping = useCallback(async () => {
    if (!firebase || !canSend || !typingPath) return;
    if (typingIdleRef.current) window.clearTimeout(typingIdleRef.current);
    if (!typingOnRef.current) return;
    typingOnRef.current = false;
    try {
      const { db } = await initFirebaseFromPayload(firebase);
      await updateDoc(doc(db, ...pathSegments(firebase.room_path)), {
        [typingPath]: false,
      });
    } catch {
      /* ignore typing write errors */
    }
  }, [canSend, firebase, typingPath]);

  const send = useCallback(
    async (raw: string) => {
      const text = raw.trim();
      if (!text || !canSend || !firebase) return;
      if (text.length > 4000) {
        throw new Error("Message is too long.");
      }

      await clearTyping();

      if (!firebase.direct_firestore_writes) {
        await httpSend?.(text);
        return;
      }

      const { db } = await initFirebaseFromPayload(firebase);
      const messagesCol = collection(db, ...pathSegments(firebase.messages_path));
      const messageRef = doc(messagesCol);
      await setDoc(messageRef, buildChatMessagePayload(firebase, text, messageRef.id));
    },
    [canSend, clearTyping, firebase, httpSend],
  );

  const setTypingFromDraft = useCallback(
    (draft: string) => {
      if (!firebase || !enabled || !canSend || !typingPath) return;

      const run = async (value: boolean) => {
        if (typingOnRef.current === value) return;
        typingOnRef.current = value;
        try {
          const { db } = await initFirebaseFromPayload(firebase);
          await updateDoc(doc(db, ...pathSegments(firebase.room_path)), {
            [typingPath]: value,
          });
        } catch {
          /* ignore typing write errors */
        }
      };

      if (draft.trim()) {
        void run(true);
        if (typingIdleRef.current) window.clearTimeout(typingIdleRef.current);
        typingIdleRef.current = window.setTimeout(() => {
          void run(false);
        }, TYPING_IDLE_MS);
      } else {
        if (typingIdleRef.current) window.clearTimeout(typingIdleRef.current);
        void run(false);
      }
    },
    [canSend, enabled, firebase, typingPath],
  );

  useEffect(() => {
    const room = firebaseRef.current;
    if (!canListen || !room) {
      setMessages([]);
      setIsPeerOnline(false);
      setPeerLastSeenAt(null);
      setIsPeerTyping(false);
      setIsConnecting(false);
      return;
    }

    let cancelled = false;
    let unsubMessages: (() => void) | undefined;
    let unsubPresence: (() => void) | undefined;
    let unsubTyping: (() => void) | undefined;
    let heartbeat: number | undefined;
    let removePageListeners: (() => void) | undefined;
    let removeAppStateListener: (() => void) | undefined;
    const session = ++presenceSession;
    const myPresencePath = ownPresencePath(room);
    const peerPresencePath = otherPresencePath(room);
    const peerTypingUid = otherParticipantUid(room);
    const myTypingPath = typingFieldPath(room.typing_field, room.uid);

    setIsConnecting(true);
    setError(null);

    const start = async () => {
      try {
        const { db, rtdb } = await initFirebaseFromPayload(room);
        if (cancelled) return;

        unsubMessages = onSnapshot(
          collection(db, ...pathSegments(room.messages_path)),
          (snapshot) => {
            const next = snapshot.docs
              .map((item) =>
                mapChatMessageDoc(item.id, item.data() as ChatMessageDoc, room.uid),
              )
              .sort((a, b) => a.createdAtMs - b.createdAtMs)
              .slice(-100);
            setMessages(next);
            setIsConnecting(false);
          },
          (snapshotError) => {
            const code = (snapshotError as { code?: string }).code;
            if (code === "permission-denied") {
              onPermissionDeniedRef.current?.();
            }
            setError(snapshotError.message);
            setIsConnecting(false);
          },
        );

        unsubTyping = onSnapshot(doc(db, ...pathSegments(room.room_path)), (snap) => {
          const data = snap.data() as { typing?: Record<string, boolean> } | undefined;
          setIsPeerTyping(Boolean(peerTypingUid && data?.typing?.[peerTypingUid]));
        });

        if (rtdb) {
          try {
            if (peerPresencePath) {
              unsubPresence = onValue(
                ref(rtdb, peerPresencePath),
                (snap) => {
                  const presence = snap.val() as Parameters<typeof isPresenceOnline>[0];
                  setIsPeerOnline(isPresenceOnline(presence));
                  setPeerLastSeenAt(parsePresenceLastSeenMs(presence));
                },
                (presenceError) => {
                  setError(presenceError.message);
                },
              );
            }

            const presenceRef = ref(rtdb, myPresencePath);
            const beat = async () => {
              await set(presenceRef, {
                online: true,
                lastSeen: rtdbServerTimestamp(),
              });
            };
            const markOffline = () => {
              void set(presenceRef, {
                online: false,
                lastSeen: rtdbServerTimestamp(),
              });
            };
            const handlePageHide = () => {
              markOffline();
            };
            const handleVisibilityChange = () => {
              if (document.visibilityState === "hidden") {
                markOffline();
              } else {
                void beat();
              }
            };
            await beat();
            await onDisconnect(presenceRef).set({
              online: false,
              lastSeen: rtdbServerTimestamp(),
            });
            heartbeat = window.setInterval(() => {
              void beat();
            }, PRESENCE_MS);
            window.addEventListener("pagehide", handlePageHide);
            document.addEventListener("visibilitychange", handleVisibilityChange);
            removePageListeners = () => {
              window.removeEventListener("pagehide", handlePageHide);
              document.removeEventListener("visibilitychange", handleVisibilityChange);
            };

            if (Capacitor.isNativePlatform()) {
              const appStateHandle = await App.addListener("appStateChange", ({ isActive }) => {
                if (!isActive) {
                  markOffline();
                } else {
                  void beat();
                }
              });
              removeAppStateListener = () => {
                void appStateHandle.remove();
              };
            }
          } catch (presenceError) {
            setError(
              presenceError instanceof Error
                ? presenceError.message
                : "Presence unavailable",
            );
          }
        }
      } catch (startError) {
        if (cancelled) return;
        setError(
          startError instanceof Error ? startError.message : "Failed to connect chat",
        );
        setIsConnecting(false);
      }
    };

    void start();

    return () => {
      cancelled = true;
      unsubMessages?.();
      unsubPresence?.();
      unsubTyping?.();
      if (heartbeat) window.clearInterval(heartbeat);
      removePageListeners?.();
      removeAppStateListener?.();
      if (typingIdleRef.current) window.clearTimeout(typingIdleRef.current);
      typingOnRef.current = false;

      void (async () => {
        if (session !== presenceSession) return;
        try {
          const { db, rtdb } = await initFirebaseFromPayload(room);
          if (session !== presenceSession) return;
          if (myTypingPath) {
            await updateDoc(doc(db, ...pathSegments(room.room_path)), {
              [myTypingPath]: false,
            });
          }
          if (rtdb) {
            await set(ref(rtdb, myPresencePath), {
              online: false,
              lastSeen: rtdbServerTimestamp(),
            });
          }
        } catch {
          /* ignore teardown errors */
        }
      })();
    };
  }, [canListen, customToken, messagesPath, presencePath, roomPath, uid]);

  return {
    messages,
    isPeerOnline,
    peerLastSeenAt,
    isPeerTyping,
    isConnecting,
    error,
    send,
    setTypingFromDraft,
  };
}
